import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { Grid, Box, Alert, Paper, Typography } from '@mui/material';
import ChatInterface, { Message } from '../../common/ChatInterface';
import StepHeader from '../../common/StepHeader';
import CodegenSpecEditor from './CodegenSpecEditor';
import {
  useWorkflow,
  type ChatHistoryMessage,
} from '../../../contexts/WorkflowContext';
import {
  workflowService,
  type ChatResponse,
  type QualityReport,
  type CodegenOutput,
  type StepStructuredData,
} from '../../../services/workflow';
import type {
  FeedbackRating,
  MessageFeedbackState,
} from '../../../types/feedback';

// Props for step data loaded from backend (for resume functionality)
interface CodeGenStepProps {
  initialChatHistory?: ChatHistoryMessage[];
  initialSpecData?: StepStructuredData | null;
  initialQualityScore?: number;
  initialQualityReport?: QualityReport;
}

// Default welcome message
const WELCOME_MESSAGE: Message = {
  id: '1',
  role: 'assistant',
  content:
    "I'll generate ADK Python code based on your technical specifications. Let me analyze the design and architecture to create working code with proper patterns...",
  timestamp: Date.now(),
};

const CodeGenStep: React.FC<CodeGenStepProps> = ({
  initialChatHistory,
  initialSpecData,
  initialQualityScore,
  initialQualityReport,
}) => {
  const { projectId, learningMode } = useWorkflow();

  // Track which messages have received feedback
  const [messageFeedback, setMessageFeedback] = useState<MessageFeedbackState>(
    {}
  );

  const handleFeedbackSubmit = useCallback(
    (messageIndex: number, rating: FeedbackRating) => {
      setMessageFeedback((prev) => ({
        ...prev,
        [messageIndex]: rating,
      }));
    },
    []
  );

  // Check if we have loaded data from a previous session
  const hasLoadedData = Boolean(
    initialChatHistory && initialChatHistory.length > 0
  );

  // Convert loaded chat history to Message format, or use welcome message
  const initialMessages = useMemo<Message[]>(() => {
    if (initialChatHistory && initialChatHistory.length > 0) {
      return initialChatHistory.map((msg, index) => ({
        id: `loaded-${index}`,
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.timestamp).getTime(),
      }));
    }
    return [WELCOME_MESSAGE];
  }, [initialChatHistory]);

  const [messages, setMessages] = useState<Message[]>(initialMessages);

  // Codegen state - initialize from loaded data
  const [codegen, setCodegen] = useState<CodegenOutput | null>(() => {
    if (
      initialSpecData &&
      'files' in initialSpecData &&
      'project_name' in initialSpecData
    ) {
      return initialSpecData as CodegenOutput;
    }
    return null;
  });

  // Quality state - initialize from loaded data
  const [qualityScore, setQualityScore] = useState<number>(
    initialQualityScore ?? 0
  );
  const [qualityReport, setQualityReport] = useState<QualityReport | null>(
    initialQualityReport ?? null
  );

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialMessageSent = useRef(hasLoadedData); // Skip auto-trigger if we have loaded data

  // Update state when initial data changes
  useEffect(() => {
    if (initialChatHistory && initialChatHistory.length > 0) {
      setMessages(
        initialChatHistory.map((msg, index) => ({
          id: `loaded-${index}`,
          role: msg.role,
          content: msg.content,
          timestamp: new Date(msg.timestamp).getTime(),
        }))
      );
      initialMessageSent.current = true;
    }
  }, [initialChatHistory]);

  useEffect(() => {
    if (
      initialSpecData &&
      'files' in initialSpecData &&
      'project_name' in initialSpecData
    ) {
      setCodegen(initialSpecData as CodegenOutput);
    }
  }, [initialSpecData]);

  useEffect(() => {
    if (initialQualityScore !== undefined) {
      setQualityScore(initialQualityScore);
    }
  }, [initialQualityScore]);

  useEffect(() => {
    if (initialQualityReport) {
      setQualityReport(initialQualityReport);
    }
  }, [initialQualityReport]);

  const handleSendMessage = useCallback(
    async (content: string) => {
      if (!projectId) {
        setError(
          'No project selected. Please create or select a project first.'
        );
        return;
      }

      const newMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, newMessage]);
      setIsLoading(true);
      setError(null);

      try {
        const response: ChatResponse = await workflowService.chat(
          projectId,
          content,
          learningMode
        );

        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: response.message,
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, assistantMessage]);

        // Update codegen from structured_data if present (type guard for CodegenOutput)
        if (
          response.structured_data &&
          'files' in response.structured_data &&
          'project_name' in response.structured_data
        ) {
          const codegenData = response.structured_data as CodegenOutput;
          setCodegen(codegenData);
        }

        // Update quality score
        if (response.quality_score !== undefined) {
          setQualityScore(response.quality_score);
        }

        // Update quality report
        if (response.quality_report) {
          setQualityReport(response.quality_report);
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to send message';
        setError(errorMessage);
        console.error('Chat error:', err);
      } finally {
        setIsLoading(false);
      }
    },
    [projectId, learningMode]
  );

  // Send initial message to trigger code generation when component mounts
  useEffect(() => {
    if (projectId && !initialMessageSent.current) {
      initialMessageSent.current = true;
      // Send a trigger message to the agent to start code generation
      handleSendMessage(
        'Please generate ADK Python code based on my technical specification.'
      );
    }
  }, [projectId, handleSendMessage]);

  // Calculate quality score as percentage
  const qualityPercent = Math.round(qualityScore * 100);

  return (
    <>
      <StepHeader step="codegen" />
      <Grid container spacing={2} sx={{ height: '100%' }}>
        {/* Left Column: Chat */}
        <Grid
          size={{ xs: 12, md: 5 }}
          sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        >
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
          <ChatInterface
            messages={messages}
            onSendMessage={handleSendMessage}
            isLoading={isLoading}
            height="calc(100vh - 250px)"
            enableFeedback={!!projectId}
            projectId={projectId || undefined}
            step="codegen"
            messageFeedback={messageFeedback}
            onFeedbackSubmit={handleFeedbackSubmit}
          />
        </Grid>

        {/* Right Column: Generated Code */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Box
            sx={{
              height: 'calc(100vh - 250px)',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {/* Codegen Spec Editor */}
            <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
              <CodegenSpecEditor
                codegen={codegen}
                qualityScore={qualityScore}
                qualityScoreLabel="Completeness"
                isLoading={isLoading}
              />
            </Box>

            {/* Quality indicator - styled based on quality score */}
            <Paper
              sx={{
                p: 2,
                flexShrink: 0,
                bgcolor:
                  qualityPercent >= 80
                    ? 'success.light'
                    : qualityPercent >= 50
                      ? 'warning.light'
                      : 'grey.100',
              }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {qualityPercent >= 80
                  ? 'Your code is ready! You can download the project and proceed to Operations planning.'
                  : qualityPercent >= 50
                    ? 'Code generation is progressing. You can proceed or ask for refinements.'
                    : codegen
                      ? 'Keep iterating to improve the generated code, or proceed when ready.'
                      : 'Chat with the agent to generate code for your agent.'}
              </Typography>
              {qualityReport &&
                qualityReport.issues &&
                qualityReport.issues.length > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
                  >
                    Suggestions: {qualityReport.issues.slice(0, 2).join(', ')}
                  </Typography>
                )}
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default CodeGenStep;
